import { Suspense, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n/I18nProvider';
import { track } from '../lib/analytics';
import {
  createCheckoutSession,
  getEntitlement,
  redeemGumroadLicense
} from '../lib/api';
import { supabase } from '../lib/supabase';

const nav = [
  ['/', 'home', 'home'],
  ['/roadmap', 'roadmap', 'roadmap'],
  ['/ai', 'ai', 'ai'],
  ['/community', 'community', 'community'],
  ['/network', 'network', 'network'],
  ['/messages', 'messages', 'messages'],
  ['/opportunities', 'opportunities', 'opportunities'],
  ['/notifications', 'notifications', 'notifications']
] as const;

export function AppShell() {
  const { m, locale, setLocale } = useI18n();
  const auth = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [isStaff, setIsStaff] = useState(false);
  const [intentMessage, setIntentMessage] = useState('');
  const [intentError, setIntentError] = useState('');
  const [activationOpen, setActivationOpen] = useState(false);
  const [activationMode, setActivationMode] = useState<'checking' | 'manual'>('checking');
  const [licenseKey, setLicenseKey] = useState('');
  const [activationBusy, setActivationBusy] = useState(false);
  const [activationError, setActivationError] = useState('');
  const handledIntent = useRef('');
  const licenseInput = useRef<HTMLInputElement | null>(null);
  const activationRun = useRef(0);
  const activeOffer = new URLSearchParams(location.search).get('offer') === 'career_sprint_30d'
    ? 'career_sprint_30d'
    : 'professional_subscription';

  useEffect(() => {
    setMobileOpen(false);
    track('app_route_viewed', { path: location.pathname, language: locale });
  }, [location.pathname, locale]);

  useEffect(() => {
    if (!auth.assessmentSaved) return;
    setIntentError('');
    setIntentMessage(
      locale === 'it'
        ? 'Nuovo risultato salvato. Profilo e percorso sono stati aggiornati.'
        : 'New result saved. Your profile and roadmap have been updated.'
    );
    const timer = window.setTimeout(() => setIntentMessage(''), 7000);
    return () => window.clearTimeout(timer);
  }, [auth.assessmentSaved, locale]);

  useEffect(() => {
    if (!auth.assessmentSyncError) return;
    setIntentMessage('');
    setIntentError(
      locale === 'it'
        ? 'Il nuovo risultato non è stato salvato. Riprova senza rifare il test.'
        : 'Your new result was not saved. Retry without retaking the assessment.'
    );
  }, [auth.assessmentSyncError, locale]);

  useEffect(() => {
    if (!auth.user) return;
    let active = true;
    async function loadUnread() {
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .is('read_at', null);
      if (active && !error) setUnreadNotifications(count || 0);
    }
    void loadUnread();
    const channel = supabase
      .channel('app-shell-notifications')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications' }, () => {
        void loadUnread();
      })
      .subscribe();
    return () => {
      active = false;
      void supabase.removeChannel(channel);
    };
  }, [auth.user]);

  useEffect(() => {
    if (!auth.user) {
      setIsStaff(false);
      return;
    }
    let active = true;
    void supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', auth.user.id)
      .in('role', ['moderator', 'admin'])
      .then(({ data, error }) => {
        if (active) setIsStaff(!error && Boolean(data?.length));
      });
    return () => {
      active = false;
    };
  }, [auth.user]);

  useEffect(() => {
    if (!activationOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !(activationBusy && activationMode === 'manual')) {
        activationRun.current += 1;
        setActivationOpen(false);
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [activationBusy, activationMode, activationOpen]);

  useEffect(() => {
    if (!auth.user) return;
    const params = new URLSearchParams(location.search);
    const intent = params.get('intent') || '';
    if (!intent || handledIntent.current === intent) return;
    handledIntent.current = intent;

    if (intent === 'activate') {
      setActivationError('');
      setActivationMode('checking');
      setActivationOpen(true);
      void waitForAutomaticActivation();
      return;
    }
    if (intent !== 'professional') return;
    setIntentError('');
    setIntentMessage(
      locale === 'it'
        ? 'Apertura del checkout sicuro…'
        : 'Opening secure checkout…'
    );
    void createCheckoutSession(locale, 'monthly', false, activeOffer)
      .then((session) => {
        track('checkout_started', {
          language: locale,
          provider: session.provider,
          source: 'app_upgrade_intent',
          offer: activeOffer,
          billing_period: activeOffer === 'career_sprint_30d' ? 'one_time' : 'monthly',
          price: activeOffer === 'career_sprint_30d' ? 19 : 14.9,
          currency: 'EUR'
        });
        window.location.assign(session.url);
      })
      .catch((cause) => {
        handledIntent.current = '';
        setIntentMessage('');
        setIntentError(
          cause instanceof Error
            ? cause.message
            : locale === 'it'
              ? 'Impossibile aprire il checkout.'
              : 'Unable to open checkout.'
        );
      });
  }, [activeOffer, auth.user, locale, location.search]);

  function clearActivationIntent() {
    const params = new URLSearchParams(location.search);
    params.delete('intent');
    params.delete('activate');
    params.delete('offer');
    const query = params.toString();
    window.history.replaceState({}, '', `${location.pathname}${query ? `?${query}` : ''}`);
  }

  async function finishMembershipActivation(
    entitlement: Awaited<ReturnType<typeof getEntitlement>>,
    activation: 'automatic_webhook' | 'manual_license'
  ) {
    const activatedOffer = entitlement.offer === 'career_sprint_30d'
      ? 'career_sprint_30d'
      : activeOffer;
    if (entitlement.conversion) {
      window.OrivonGoogleAds?.trackPurchase(entitlement.conversion);
    }
    track(activatedOffer === 'career_sprint_30d' ? 'career_sprint_activated' : 'subscription_activated', {
      language: locale,
      provider: entitlement.provider || (activatedOffer === 'career_sprint_30d' ? 'gumroad_order' : 'gumroad'),
      activation,
      status: entitlement.status,
      offer: activatedOffer
    });
    setActivationOpen(false);
    setActivationBusy(false);
    setLicenseKey('');
    setIntentMessage(
      locale === 'it'
        ? activatedOffer === 'career_sprint_30d'
          ? 'Il tuo Career Sprint di 30 giorni è attivo.'
          : 'Orivon Professional è attivo.'
        : activatedOffer === 'career_sprint_30d'
          ? 'Your 30-day Career Sprint is active.'
          : 'Orivon Professional is active.'
    );
    clearActivationIntent();
    window.setTimeout(() => window.location.reload(), 700);
  }

  async function waitForAutomaticActivation() {
    const run = activationRun.current + 1;
    activationRun.current = run;
    setActivationMode('checking');
    setActivationBusy(true);
    setActivationError('');
    for (let attempt = 0; attempt < 16; attempt += 1) {
      if (run !== activationRun.current) return;
      if (attempt > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, 2500));
      }
      if (run !== activationRun.current) return;
      try {
        const entitlement = await getEntitlement(true);
        if (entitlement.professional) {
          await finishMembershipActivation(entitlement, 'automatic_webhook');
          return;
        }
      } catch (_) {
        // Gumroad can take a few seconds to deliver the purchase notification.
      }
    }
    if (run !== activationRun.current) return;
    setActivationBusy(false);
    setActivationMode('manual');
    setActivationError(
      locale === 'it'
        ? 'L’attivazione automatica sta richiedendo più tempo del previsto. Puoi usare la chiave della ricevuta come recupero.'
        : 'Automatic activation is taking longer than expected. You can use the receipt license key as a recovery option.'
    );
    window.setTimeout(() => licenseInput.current?.focus(), 50);
  }

  async function activateMembership() {
    const normalized = licenseKey.trim().toUpperCase();
    if (!/^[A-Z0-9-]{16,80}$/.test(normalized)) {
      setActivationError(
        locale === 'it'
          ? 'Inserisci la chiave di licenza presente nella ricevuta Gumroad.'
          : 'Enter the license key from your Gumroad receipt.'
      );
      licenseInput.current?.focus();
      return;
    }
    setActivationBusy(true);
    setActivationError('');
    try {
      const result = await redeemGumroadLicense(normalized, activeOffer);
      const entitlement = await getEntitlement(true);
      if (!result.professional || !entitlement.professional) {
        throw new Error(
          locale === 'it'
            ? 'L’abbonamento non risulta attivo.'
            : 'The membership is not active.'
        );
      }
      await finishMembershipActivation(entitlement, 'manual_license');
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : '';
      const localizedMessage = /same email/i.test(message)
        ? locale === 'it'
          ? 'Usa la stessa email per Orivon e Gumroad, poi riprova.'
          : 'Use the same email for Orivon and Gumroad, then try again.'
        : /not active|inactive|cancelled|canceled|ended/i.test(message)
          ? locale === 'it'
            ? 'Questo abbonamento Gumroad non risulta attivo.'
            : 'This Gumroad membership is not active.'
          : /license|verified|verify/i.test(message)
            ? locale === 'it'
              ? 'La chiave non è valida o non appartiene all’offerta Orivon selezionata.'
              : 'The key is invalid or does not belong to the selected Orivon offer.'
            : message;
      setActivationError(
        localizedMessage || (locale === 'it'
          ? 'Non è stato possibile verificare l’abbonamento.'
          : 'The membership could not be verified.')
      );
    } finally {
      setActivationBusy(false);
    }
  }

  return (
    <div className="app-shell">
      <a className="skip-link" href="#app-main-content">
        {locale === 'it' ? 'Vai al contenuto principale' : 'Skip to main content'}
      </a>
      <header className="mobile-header">
        <a className="brand" href={locale === 'it' ? '/it/' : '/'}>
          <img src="/favicon.svg" alt="" />
          <span>Orivon</span>
        </a>
        <button
          type="button"
          className="icon-button"
          aria-controls="app-sidebar"
          aria-expanded={mobileOpen}
          aria-label={
            mobileOpen
              ? locale === 'it' ? 'Chiudi menu' : 'Close menu'
              : locale === 'it' ? 'Apri menu' : 'Open menu'
          }
          onClick={() => setMobileOpen((value) => !value)}
        >
          <Icon name={mobileOpen ? 'close' : 'menu'} />
        </button>
      </header>

      <aside className={`sidebar ${mobileOpen ? 'open' : ''}`} id="app-sidebar">
        <a className="brand desktop-brand" href={locale === 'it' ? '/it/' : '/'}>
          <img src="/favicon.svg" alt="" />
          <span>Orivon</span>
        </a>
        <div className="product-label">{m.common.appName}</div>
        <nav aria-label={locale === 'it' ? 'Applicazione' : 'Application'}>
          {nav.map(([to, key, icon]) => (
            <NavLink key={to} to={to} end={to === '/'}>
              <Icon name={icon} />
              <span>{m.nav[key]}</span>
              {key === 'notifications' && unreadNotifications ? (
                <i className="nav-count">{Math.min(unreadNotifications, 99)}</i>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <a
            href={locale === 'it' ? '/it/?retake=1' : '/?retake=1'}
            onClick={() => track('retake_assessment_clicked', {
              language: locale,
              source: 'professional_app'
            })}
          >
            <Icon name="retake" />
            <span>{locale === 'it' ? 'Rifai il test' : 'Retake assessment'}</span>
          </a>
          <NavLink to="/settings">
            <Icon name="settings" />
            <span>{m.nav.settings}</span>
          </NavLink>
          {isStaff ? (
            <NavLink to="/admin">
              <Icon name="settings" />
              <span>{m.nav.admin}</span>
            </NavLink>
          ) : null}
          <div className="locale-switch" aria-label={locale === 'it' ? 'Lingua' : 'Language'}>
            <button type="button" aria-pressed={locale === 'en'} className={locale === 'en' ? 'active' : ''} onClick={() => setLocale('en')}>EN</button>
            <button type="button" aria-pressed={locale === 'it'} className={locale === 'it' ? 'active' : ''} onClick={() => setLocale('it')}>IT</button>
          </div>
          <button type="button" className="signout" onClick={() => auth.signOut()}>{m.common.signOut}</button>
        </div>
      </aside>

      {mobileOpen ? (
        <button
          type="button"
          className="mobile-backdrop"
          aria-label={locale === 'it' ? 'Chiudi menu' : 'Close menu'}
          onClick={() => setMobileOpen(false)}
        />
      ) : null}
      <main className="app-content" id="app-main-content" tabIndex={-1}>
        {intentMessage ? (
          <div className="intent-banner" role="status">{intentMessage}</div>
        ) : null}
        {intentError ? (
          <div className="intent-banner error" role="alert">
            <span>{intentError}</span>
            <button
              type="button"
              onClick={() => {
                handledIntent.current = '';
                window.location.reload();
              }}
            >
              {locale === 'it' ? 'Riprova' : 'Try again'}
            </button>
          </div>
        ) : null}
        <Suspense
          fallback={
            <div className="page-state route-loading" role="status">
              <div className="loading-mark" aria-hidden="true" />
              <p>{m.common.loading}</p>
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>

      <nav className="bottom-nav" aria-label={locale === 'it' ? 'Principale' : 'Primary'}>
        {nav.slice(0, 5).map(([to, key, icon]) => (
          <NavLink key={to} to={to} end={to === '/'}>
            <Icon name={icon} size={19} />
            <span>{m.nav[key]}</span>
          </NavLink>
        ))}
      </nav>

      {activationOpen ? (
        <div
          className="modal-backdrop"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget &&
              !(activationBusy && activationMode === 'manual')
            ) {
              activationRun.current += 1;
              setActivationOpen(false);
            }
          }}
        >
          <section
            className="review-modal activation-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="activation-dialog-title"
            aria-describedby="activation-dialog-description"
          >
            <div className="panel-heading">
              <div>
                <span className="eyebrow">
                  {activeOffer === 'career_sprint_30d' ? 'Orivon Career Sprint' : 'Orivon Professional'}
                </span>
                <h2 id="activation-dialog-title">
                  {activationMode === 'checking'
                    ? activeOffer === 'career_sprint_30d'
                      ? locale === 'it'
                        ? 'Stiamo attivando il tuo Sprint'
                        : 'We’re activating your Sprint'
                      : locale === 'it'
                        ? 'Stiamo attivando Professional'
                        : 'We’re activating Professional'
                    : locale === 'it'
                      ? activeOffer === 'career_sprint_30d'
                        ? 'Recupera il tuo acquisto'
                        : 'Recupera il tuo abbonamento'
                      : activeOffer === 'career_sprint_30d'
                        ? 'Recover your purchase'
                        : 'Recover your membership'}
                </h2>
              </div>
              <button
                type="button"
                className="modal-close"
                aria-label={m.common.cancel}
                disabled={activationBusy && activationMode === 'manual'}
                onClick={() => {
                  activationRun.current += 1;
                  setActivationOpen(false);
                }}
              >
                ×
              </button>
            </div>
            <p id="activation-dialog-description">
              {activationMode === 'checking'
                ? locale === 'it'
                  ? 'Stiamo collegando automaticamente l’acquisto Gumroad al tuo account Orivon. Non devi copiare nessuna chiave.'
                  : 'We’re automatically linking your Gumroad purchase to your Orivon account. You do not need to copy a key.'
                : locale === 'it'
                  ? 'Se l’attivazione automatica non è arrivata, usa la chiave della ricevuta o della Libreria Gumroad con la stessa email del tuo account Orivon.'
                  : 'If automatic activation did not arrive, use the key from your Gumroad receipt or Library with the same email as your Orivon account.'}
            </p>
            {activationMode === 'checking' ? (
              <>
                <div className="activation-progress" role="status">
                  <span className="activation-spinner" aria-hidden="true" />
                  <span>
                    {locale === 'it'
                      ? 'Verifica sicura in corso…'
                      : 'Secure verification in progress…'}
                  </span>
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="button secondary"
                    onClick={() => {
                      activationRun.current += 1;
                      setActivationBusy(false);
                      setActivationMode('manual');
                      setActivationError('');
                      window.setTimeout(() => licenseInput.current?.focus(), 50);
                    }}
                  >
                    {locale === 'it' ? 'Usa la chiave manualmente' : 'Use the key manually'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <label htmlFor="gumroad-license-key">
                  {locale === 'it' ? 'Chiave di licenza Gumroad' : 'Gumroad license key'}
                  <input
                    ref={licenseInput}
                    id="gumroad-license-key"
                    type="text"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                    value={licenseKey}
                    disabled={activationBusy}
                    onChange={(event) => setLicenseKey(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') void activateMembership();
                    }}
                  />
                </label>
                {activationError ? <p className="form-error" role="alert">{activationError}</p> : null}
                <p className="activation-privacy-note">
                  {locale === 'it'
                    ? 'La chiave viene verificata dal server e non viene salvata nel browser.'
                    : 'The key is verified by the server and is never stored in your browser.'}
                </p>
                <div className="modal-actions">
                  <a
                    className="button secondary"
                    href="https://app.gumroad.com/library"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {locale === 'it' ? 'Apri Libreria Gumroad' : 'Open Gumroad Library'}
                  </a>
                  <button
                    type="button"
                    className="button primary"
                    disabled={activationBusy}
                    onClick={() => void activateMembership()}
                  >
                    {activationBusy
                      ? m.common.loading
                      : locale === 'it' ? 'Verifica e attiva' : 'Verify and activate'}
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}
